import { Flex, Icon } from "@chakra-ui/react";
import CDS from "design-system";
import { TbChecks, TbAlertTriangle, TbCircleX, TbCircleMinus, TbClock } from "react-icons/tb";
import { dateInFormat } from "helpers/time";

const STATUS_CONFIG: Record<string, { bg: string; borderColor: string; color: string; icon: any; label: string }> = {
  complete: { bg: "green.50", borderColor: "green.100", color: "green.600", icon: TbChecks, label: "Good to Go" },
  partial: {
    bg: "orange.50",
    borderColor: "orange.100",
    color: "orange.500",
    icon: TbAlertTriangle,
    label: "Needs a Nudge",
  },
  behind: { bg: "red.50", borderColor: "red.100", color: "red.600", icon: TbCircleX, label: "Behind Schedule" },
  skipped: { bg: "gray.50", borderColor: "gray.200", color: "gray.500", icon: TbCircleMinus, label: "Skipped" },
};

interface CheckpointStatusBarProps {
  data: any;
}

export default function CheckpointStatusBar({ data }: CheckpointStatusBarProps) {
  const isEvaluated = data.evaluatedAt != null;
  const isPending = !isEvaluated && data.status === "pending";

  if (isPending) {
    return (
      <Flex align="center" gap="2" bg="gray.50" borderBottom="1px solid" borderColor="gray.200" px="5" py="4" mx="-5">
        <Icon as={TbClock} boxSize="4" color="gray.500" />
        <CDS.Text12 fontWeight="500" color="content.secondary">
          Scheduled · runs {dateInFormat(data.evaluateAt, "ddd, MMM D [at] h:mm A", data.timezone)}
        </CDS.Text12>
      </Flex>
    );
  }

  const config = STATUS_CONFIG[data.status] || STATUS_CONFIG.complete;

  const reason = (() => {
    if (data.status === "behind") {
      const overdue = data.tasksOverdue || 0;
      const complete = data.tasksComplete || 0;
      const total = data.tasksTotal || 0;
      return `${overdue} overdue task${overdue !== 1 ? "s" : ""}, ${complete} of ${total} completed`;
    }
    if (data.status === "partial") {
      const complete = data.tasksComplete || 0;
      const total = data.tasksTotal || 0;
      const remaining = total - complete;
      return `${complete} of ${total} tasks completed, ${remaining} still open`;
    }
    return null;
  })();

  return (
    <Flex
      direction="column"
      gap="0.5"
      bg={config.bg}
      borderBottom="1px solid"
      borderColor={config.borderColor}
      px="5"
      py="4"
      mx="-5"
    >
      <Flex align="center" gap="2">
        <Icon as={config.icon} boxSize="4" color={config.color} />
        <CDS.Text12 fontWeight="600" color={config.color}>
          {config.label}
        </CDS.Text12>
      </Flex>
      {reason && (
        <CDS.Text10 color={config.color} pl="6" opacity={0.8}>
          {reason}
        </CDS.Text10>
      )}
    </Flex>
  );
}
